<script setup lang="ts">
    import KForm from "@/components/forms/Form/KForm.vue";
    import { flashWarning } from "@/utils";
    import { AxiosError } from "axios";
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs, watch } from "vue";
    import { useStore } from "@/store";
    import emitter from "tiny-emitter/instance";

    // TODO Ändra till användning av type i materialModule i Vue3
    interface Leverantor {
        id?: string;
        namn: string;
    }

    const store = useStore();

    const props = defineProps<{
        tabId: number; // TODO Ändra i Vue3
        parameters?: {
            leverantorId: string;
        };
    }>();

    const { parameters, tabId } = toRefs(props);
    const errors = ref<{
        [key: string]: string[];
    }>({});
    const formRef = ref();

    const isEditing = computed(() => !!leverantor.value);

    const leverantor = computed(() => {
        return store.getters["leverantor/leverantorById"]({
            id: parameters?.value?.leverantorId,
        });
    });
    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData());
    const heading = computed(() => (isEditing.value ? "Redigera leverantör" : "Skapa leverantör"));
    const submitLabel = computed(() => (isEditing.value ? "Redigera leverantör" : "Lägg till leverantör"));

    defineExpose({
        hasUnsavedData,
        tabId,
    });

    // TODO convert to useTabs in v3
    const closeTab = ({
        override,
        hasUnsavedData,
    }: {
        override?: boolean;
        hasUnsavedData?: boolean;
    } = {}) => {
        if (!override && hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const onSubmit = async (newLeverantor: unknown) => {
        try {
            if (isEditing.value) {
                await store.dispatch("leverantor/updateLeverantor", { leverantor: newLeverantor as Leverantor });
            } else {
                await store.dispatch("leverantor/createLeverantor", { leverantor: newLeverantor as Leverantor });
            }

            closeTab({ override: true });
        } catch (thrownError) {
            const error = thrownError as AxiosError;

            if (error?.response?.data) {
                errors.value = error.response.data;

                return;
            }

            flashWarning(isEditing.value ? "Kunde inte redigera leverantören" : "Kunde inte lägga till leverantör");
        }
    };

    const onCancel = async ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        closeTab({ hasUnsavedData });
    };

    const initForm = async () => {
        if (!parameters?.value?.leverantorId) {
            return null;
        }

        return store.getters["leverantor/leverantorById"]({
            id: parameters?.value?.leverantorId,
        });
    };

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: isEditing.value ? "Redigera leverantör" : "Skapa leverantör",
        });
    };

    onBeforeMount(async () => {
        await store.dispatch("leverantor/addSubscriber");
        await store.dispatch("material/addSubscriber");

        setTabName();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("leverantor/removeSubscriber");
        await store.dispatch("material/removeSubscriber");
    });
</script>

<template>
    <KForm
        ref="formRef"
        id="leverantor-form"
        class="material-form"
        :initial-values-getter="initForm"
        :submit-content="submitLabel"
        cancel-content="Avbryt"
        :disableSubmit="!hasUnsavedData"
        :errors="errors"
        columns="3"
        @submit="onSubmit"
        @cancel="onCancel"
    >
        <template #header>
            <h2>{{ heading }}</h2>
        </template>
        <template #default>
            <div class="form-content">
                <KText required type="text" label="Namn" name="namn" />
            </div>
        </template>
    </KForm>
</template>

<style lang="scss" scoped></style>
